import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { WeaponSelectionSchema } from '@/features/ui';
import { deleteAttachmentFiles } from '@/features/ui/DocumentsData/ui/utils';
import { SelectableList } from '@/features/ui/SelectableList';
import { HealthDataSchema } from '@/features/ui/Steps/HealthData/model/types';
import { LearningDocumentSchema } from '@/features/ui/Steps/LearningDocument/model/types';

import { ConditionView, FormSelect, Grid, Informer, Loader, StepControlPanel } from '@/shared/ui';

import { MinUserAgeMessage } from '@/components/Forms/ServiceUnAvailable/Messages';

import { useAsyncDataLoader } from '@/hooks/useAsyncDataLoader';
import { useServiceCheckAction } from '@/hooks/useServiceCheckAction';

import { renderWeapon } from '@/helpers/renderWeapon';

import ServiceAvailabilityContext, { Reason } from '@/contexts/ServiceAvailability/ServiceAvailabilityContext';

import { PurchaseItemIdMapping, Weapon } from '@/api/types';

import api from '@/api';

import {
  ADDITIONAL_OBJECT_OF_PURCHASE_OPTIONS,
  HEALTH_DOC_STEP_NUMBER,
  LEARNING_DOCUMENT_STEP_NUMBER,
  STEP_NUMBER_PERSON_STATUS,
} from '../../lib/constants';
import { ObjectOfPurchaseSchema, PersonStatusDocumentsSchema } from '../../model/types';
import { STEPS } from '../../WeaponPurchasePermit';

export const ObjectOfPurchase: React.FC<StepProps> = ({ serviceCode, next, currentRequestId }) => {
  const { control, watch, setValue, getValues } = useFormContext<
    ObjectOfPurchaseSchema & PersonStatusDocumentsSchema & LearningDocumentSchema & HealthDataSchema
  >();
  const { disableCause } = useContext(ServiceAvailabilityContext);
  const { isLoading: isActionValidating, checkActionResult, checkAction } = useServiceCheckAction('MIN_USER_AGE');
  const [isNextStepLoading, setIsNextStepLoading] = useState<boolean>(false);
  const [hasObjectOfPurchaseChanged, setHasObjectOfPurchaseChanged] = useState<boolean>(false);

  const npu = watch('orgNpu');
  const objectOfPurchase = watch('objectOfPurchase');
  const additionalObjectOfPurchase = watch('additionalObjectOfPurchase');
  const isWeaponSelectRequired = useMemo<boolean>(
    () => additionalObjectOfPurchase?.id === PurchaseItemIdMapping.WEAPONS_MAIN_PART,
    [additionalObjectOfPurchase],
  );

  const {
    isLoading: areWeaponsLoading,
    data: weapons,
    load: loadWeapons,
  } = useAsyncDataLoader(
    async () => {
      const { data } = await api.services.serviceWeapons(serviceCode as any);

      return data?.ipsItems || [];
    },
    { defaultDataValue: [] },
  );
  const {
    isLoading: areOrgNpuLoading,
    data: orgNpu,
    load: loadOrgNpu,
  } = useAsyncDataLoader(
    async () => {
      const { data } = await api.dictionary.getAuthorities();

      return data?.map((org) => ({
        id: org.id,
        label: org.name,
        isPayable: org.isPayable,
      }));
    },
    { defaultDataValue: [] },
  );

  const {
    isLoading: arePurchaseItemsLoading,
    data: purchaseItems,
    load: loadPurchaseItems,
  } = useAsyncDataLoader(async () => {
    const { data } = await api.dictionary.getPurchaseItems();
    return data?.map((purchaseItem) => ({
      id: String(purchaseItem.id),
      label: purchaseItem.name,
    }));
  });

  useEffect(() => {
    loadPurchaseItems();
    loadOrgNpu();

    STEPS.forEach((step) => (step.skipped = false));
  }, []);

  useEffect(() => {
    isWeaponSelectRequired && loadWeapons();
  }, [isWeaponSelectRequired]);

  useEffect(() => {
    if (!objectOfPurchase) return;

    if (
      [PurchaseItemIdMapping.HUNTING_RIFLED_WEAPONS, PurchaseItemIdMapping.HUNTING_SMOOTHBORE_WEAPONS].includes(
        objectOfPurchase?.id as PurchaseItemIdMapping,
      )
    ) {
      additionalObjectOfPurchase &&
        checkAction({
          serviceCode: serviceCode as any,
          typeId: additionalObjectOfPurchase.id,
          userRequestId: currentRequestId ?? '',
        });
    } else {
      checkAction({
        serviceCode: serviceCode as any,
        typeId: objectOfPurchase.id,
        userRequestId: currentRequestId ?? '',
      });
    }
  }, [objectOfPurchase, additionalObjectOfPurchase]);

  useEffect(() => {
    if (!checkActionResult.is_permitted) {
      disableCause({
        reason: Reason.MIN_USER_AGE,
        message: (
          <MinUserAgeMessage
            serviceCode={serviceCode as any}
            minAge={checkActionResult.minAge}
            unavailableObjectToRequest={objectOfPurchase.label ?? ''}
          />
        ),
      });
    }
  }, [checkActionResult, objectOfPurchase]);

  const handleNextStep = useCallback(async () => {
    if (!objectOfPurchase) return;
    setIsNextStepLoading(true);

    if (hasObjectOfPurchaseChanged) {
      const { personStatusDocuments, learningDocument, firearmRegistrationDocument, healthDataDocument } = getValues();

      personStatusDocuments?.forEach((statusDocuments) =>
        deleteAttachmentFiles(statusDocuments, currentRequestId ?? ''),
      );
      [...(personStatusDocuments ?? []), learningDocument, healthDataDocument, firearmRegistrationDocument].forEach(
        (documents) => deleteAttachmentFiles(documents ?? [], currentRequestId ?? ''),
      );
      setValue('personStatus', null as any);
      setValue('personStatusDocuments', []);
      setValue('learningDocument', []);
      setValue('healthDataDocument', []);
      setValue('firearmRegistrationDocument', []);
    }

    if (additionalObjectOfPurchase?.id === PurchaseItemIdMapping.WEAPONS_MAIN_PART) {
      [STEP_NUMBER_PERSON_STATUS, LEARNING_DOCUMENT_STEP_NUMBER, HEALTH_DOC_STEP_NUMBER].forEach(
        (step) => (STEPS[step - 1].skipped = true),
      );
      next();
      return;
    }

    switch (objectOfPurchase.id) {
      case PurchaseItemIdMapping.GAS_REVOLVERS:
        setValue('hasValidPermit', false);
        STEPS[STEP_NUMBER_PERSON_STATUS - 1].skipped = true;
        next();
        return;

      case PurchaseItemIdMapping.COLD_WEAPON:
      case PurchaseItemIdMapping.BLANK_WEAPON:
        setValue('hasValidPermit', false);
        next();
        return;

      case PurchaseItemIdMapping.HUNTING_RIFLED_WEAPONS:
      case PurchaseItemIdMapping.HUNTING_SMOOTHBORE_WEAPONS:
      case PurchaseItemIdMapping.PNEUMATIC_WEAPONS:
      case PurchaseItemIdMapping.DEVICE:
        const {
          data: { has_valid_permit },
        } = await api.erz.services.checkPurchasePermit(objectOfPurchase?.id ?? '', objectOfPurchase?.label ?? '');
        setValue('hasValidPermit', has_valid_permit);
        if (has_valid_permit) {
          if (objectOfPurchase.id === PurchaseItemIdMapping.DEVICE) {
            next();
            return;
          }
          [STEP_NUMBER_PERSON_STATUS, LEARNING_DOCUMENT_STEP_NUMBER, HEALTH_DOC_STEP_NUMBER].forEach(
            (step) => (STEPS[step - 1].skipped = true),
          );
          next();
        } else {
          next();
        }
    }

    setIsNextStepLoading(false);
  }, [objectOfPurchase]);

  const isLoading = arePurchaseItemsLoading || areOrgNpuLoading || isActionValidating || isNextStepLoading;

  return (
    <ConditionView if={!isLoading} else={<Loader />}>
      <FormBlock title="Мета звернення">
        <FormSelect
          control={control}
          name="objectOfPurchase"
          label="Предмет придбання"
          options={purchaseItems ?? []}
          onChange={() => {
            setValue('additionalObjectOfPurchase', null);
            setValue('weapon', null);
            setHasObjectOfPurchaseChanged(true);
          }}
          required
        />
        <ConditionView
          if={
            objectOfPurchase?.id === PurchaseItemIdMapping.HUNTING_RIFLED_WEAPONS ||
            objectOfPurchase?.id === PurchaseItemIdMapping.HUNTING_SMOOTHBORE_WEAPONS
          }
        >
          <FormSelect
            control={control}
            name="additionalObjectOfPurchase"
            label="Уточнення предмету придбання"
            options={ADDITIONAL_OBJECT_OF_PURCHASE_OPTIONS(objectOfPurchase?.id ?? '')}
            onChange={() => {
              setHasObjectOfPurchaseChanged(true);
              setValue('weapon', null);
            }}
            required
          />
        </ConditionView>
        <ConditionView if={isWeaponSelectRequired}>
          <ConditionView if={!areWeaponsLoading} else={<Loader />}>
            <ConditionView
              if={Boolean(weapons?.length)}
              else={
                <Informer
                  className="confirmation-orgnpu-informer"
                  icon={{
                    name: 'attention',
                    color: 'red',
                  }}
                  header={{
                    text: 'У вас немає зброї для придбання частин',
                    color: 'red',
                  }}
                  text={{
                    color: 'black',
                    text: 'Виберіть інший предмет придбання',
                  }}
                />
              }
            >
              <Controller
                name="weapon"
                control={control}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <SelectableList<Weapon, WeaponSelectionSchema['weapon']>
                    inputType="single"
                    items={weapons ?? []}
                    value={value}
                    onChange={onChange}
                    getItemId={(weapon) => weapon.id}
                    renderItem={(weapon) => <div className="weapon-list__weapon">{renderWeapon(weapon)}</div>}
                    containerClassName="weapon-list"
                    itemClassName="weapon-list__item"
                    errorMessage={error?.message}
                  />
                )}
              />
            </ConditionView>
          </ConditionView>
        </ConditionView>
      </FormBlock>
      <FormBlock title="Орган НПУ">
        <FormSelect
          control={control}
          name="orgNpu"
          label="Орган НПУ, до якого надсилається заява"
          options={orgNpu ?? []}
          required
        />
        <ConditionView if={npu && !npu.isPayable}>
          <Informer
            className="confirmation-orgnpu-informer"
            icon={{
              name: 'attention',
              color: 'red',
            }}
            header={{
              text: 'Зверніть увагу!',
              color: 'red',
            }}
            text={{
              color: 'black',
              text: 'Для цього органу НПУ тимчасово відсутня можливість оплати послуги онлайн. Для того щоб подати цю заявку вам доведеться оплатити послугу будь-яким іншим способом та прикріпити квитанцію про платіж на кроці оплати.',
            }}
          />
        </ConditionView>
      </FormBlock>
      <StepControlPanel next={handleNextStep} />
    </ConditionView>
  );
};

export const ObjectOfPurchasePreview: React.FC = () => {
  const { getValues } = useFormContext<ObjectOfPurchaseSchema>();
  const { orgNpu, weapon, objectOfPurchase, additionalObjectOfPurchase } = getValues();
  const weaponData = weapon as Nullable<Weapon>;
  const weaponModel = !weaponData
    ? null
    : weaponData.weaponType === 'firearm_part'
      ? (weaponData.ORNM ?? null)
      : (weaponData.ORMD ?? null);

  const weaponManufacturer = weaponData && weaponData.weaponType !== 'firearm_part' ? (weaponData.ORIZ ?? null) : null;

  const previewData: Array<[string, Nullable<string>]> = [
    ['Тип зброї', weaponData?.ORVD ?? null],
    ['Модель', weaponModel],
    ['Серія та номер', weaponData?.ORSR ?? null],
    ['Виробник', weaponManufacturer],
  ];

  const orgNpuPreview: Array<[string, Nullable<string>]> = [['Орган НПУ, до якого надсилається заявка', orgNpu?.label]];

  return (
    <>
      <Grid
        title="Мета звернення"
        data={[
          [
            'Предмет придбання',
            additionalObjectOfPurchase?.id === PurchaseItemIdMapping.WEAPONS_MAIN_PART
              ? additionalObjectOfPurchase?.label
              : objectOfPurchase?.label,
          ],
          ...(weaponData ? previewData : []),
          ...orgNpuPreview,
        ]}
      />
    </>
  );
};
